import { OpenAPIV3 } from 'openapi-types';
import { SchemaObject } from '@/types/openapi';
import { OpenApiExtensions } from '@/constant/Extensions';
import { RequestSampleEnum } from './Enum';

export class RequestSampleAttribute {
  isResourceAttribute: boolean;
  name?: string;
  id?: string;
  subResourceName?: string;
  description!: string;
  enum?: RequestSampleEnum[];

  format?: string;
  type?: string;
  example?: string;

  isArray: boolean = false;
  isResource?: boolean;
  resourceId?: string;

  isGlobalEnum?: boolean;
  isMoneyColumn?: boolean;
  isExternalEnum?: boolean;
  isSdkGenSeparate?: boolean;

  attributes?: RequestSampleAttribute[];

  sdkFilterName?: string;
  isCompositeArray?: boolean;
  isDependentAttribute?: boolean;
  isFilterAttribute?: boolean;
  #required: string[] | boolean;
  #propertyDefn?: SchemaObject;
  #deprecatedEnum?: string[];
  items?: {
    required: string[];
    format: string;
    isMoneyColumn: boolean;
    type?: string;
    sdkFilterName?: string;
  };
  in?: string;

  constructor(
    isResourceAttribute: boolean,
    name?: string,
    schema?: SchemaObject,
    isArray: boolean = false
  ) {
    this.isResourceAttribute = isResourceAttribute;
    this.name = name;
    this.id = name;
    this.#required = this.handleRequiredProp(schema?.required, this.name);
    this.#propertyDefn = schema;
    if (schema) {
      if (schema[OpenApiExtensions.DEPRECATED_ENUM_VALUES]) {
        this.#deprecatedEnum = (
          schema[OpenApiExtensions.DEPRECATED_ENUM_VALUES] as string
        ).split(',');
      }
      if (schema[OpenApiExtensions.RESOURCE_PATH_NAME]) {
        this.isResource = true;
        this.resourceId = schema[
          OpenApiExtensions.RESOURCE_PATH_NAME
        ] as string;
      }
      this.isFilterAttribute = schema[
        OpenApiExtensions.IS_FILTER_PARAMETER
      ] as boolean;
      if (
        schema.properties != null &&
        Object.values(schema.properties as Record<string, SchemaObject>).filter(
          (x: SchemaObject) => x[OpenApiExtensions.IS_FILTER_PARAMETER]
        ).length > 0
      ) {
        this.isFilterAttribute = true;
      }

      this.sdkFilterName = schema[OpenApiExtensions.FILTER_NAME] as string;
      this.isGlobalEnum = schema[OpenApiExtensions.IS_GLOBAL_ENUM] as boolean;
      this.isMoneyColumn = schema[OpenApiExtensions.IS_MONEY_COLUMN] as boolean;
      this.isExternalEnum = schema[
        OpenApiExtensions.IS_EXTERNAL_ENUM
      ] as boolean;
      this.isSdkGenSeparate = schema[
        OpenApiExtensions.IS_SDK_GEN_SEPARATE
      ] as boolean;
      this.isCompositeArray = schema[
        OpenApiExtensions.IS_COMPOSITE_ARRAY_REQUEST_BODY
      ] as boolean;
      this.isDependentAttribute = schema[
        OpenApiExtensions.IS_DEPENDANT_ATTRIBUTE
      ] as boolean;
    }
    this.isArray = isArray;
  }

  handleRequiredProp(
    required: boolean | string[] | null | undefined,
    name: string | undefined
  ) {
    if (required == null) return [];
    if (Array.isArray(required)) {
      return required;
    }
    if (required && name != null) return [name];
    return [];
  }

  static handleAttributeType(specType: string, inProperties: boolean) {
    if (specType === 'string' && !inProperties) {
      return 'list of string';
    }
    return specType;
  }

  static mapArrayAttributes(
    isResourceAttribute: boolean,
    schema?: SchemaObject,
    name?: string
  ) {
    return new RequestSampleAttribute(
      isResourceAttribute,
      name,
      schema,
      true
    ).toJson();
  }

  static parse(
    isResourceAttribute: boolean,
    properties: OpenAPIV3.ParameterObject[] = []
  ) {
    if (properties) {
      return properties
        .map((property: OpenAPIV3.ParameterObject) => {
          const schema = property.schema as SchemaObject;
          if (
            schema.type == 'array' &&
            schema.items &&
            Object.keys(schema.items).length > 0
          ) {
            return this.mapArrayAttributes(
              isResourceAttribute,
              schema,
              property.name
            );
          }
          return new RequestSampleAttribute(
            isResourceAttribute,
            property.name,
            schema,
            schema.type === 'array'
          ).toJson();
        })
        .filter(
          (attribute): attribute is RequestSampleAttribute => !!attribute
        );
    }
  }

  toJson() {
    if (
      this.#propertyDefn != null &&
      !this.#propertyDefn.deprecated &&
      this.name
    ) {
      this.subResourceName = this.#propertyDefn[
        OpenApiExtensions.SUB_RESOURCE_NAME
      ] as string;
      this.enum = this.#propertyDefn.enum;
      this.fetchDescription();
      if (this.isResource == null && this.#propertyDefn?.properties != null) {
        const properties =
          (this.#propertyDefn.properties as Record<string, SchemaObject>) ?? {};
        this.attributes = RequestSampleAttribute.parse(
          this.isResourceAttribute,
          Object.entries(properties).map(([key, value]) => {
            return {
              name: key,
              schema: value,
              in: 'body',
            };
          })
        );
      }
      this.example = this.#propertyDefn.example;
      this.format = this.#propertyDefn.format;
      this.type = this.#propertyDefn.type;
      if (this.format == null && this.type == 'boolean') {
        this.format = 'boolean';
      }
      return this;
    }
    return undefined;
  }

  fetchDescription() {
    if (this.#propertyDefn == null) return;
    if (this.#propertyDefn.enum != null && this.#propertyDefn.description) {
      const overallDescription = this.#propertyDefn.description.split('*');
      const enumMap: Map<string, string> = new Map<string, string>();
      for (let i = 1; i < overallDescription.length; i++) {
        const enumDescriptions: string[] = overallDescription[i]
          .split('-')
          .map((en: string) => en.trim());
        enumMap.set(enumDescriptions[0], enumDescriptions.slice(1).join('-'));
      }
      this.description = overallDescription[0].trim();
      this.enum = this.#propertyDefn.enum.map((en: string) => {
        return new RequestSampleEnum(en, enumMap.get(en));
      });
      if (this.#deprecatedEnum) {
        this.enum = this.enum?.filter(
          (en) => !this.#deprecatedEnum!.includes(en.name!)
        );
      }
    } else {
      this.description = this.#propertyDefn.description ?? '';
      if (Array.isArray(this.#propertyDefn.enum)) {
        this.enum = this.#propertyDefn.enum.map(
          (en: string) => new RequestSampleEnum(en, '')
        );
      }
    }
  }
}
